import React, { useState, useEffect, useCallback } from 'react'
import { formatNumber } from '@utils/numberFormatter.util'
import { Accordion } from 'react-bootstrap'
import { TooltipComponent } from '@syncfusion/ej2-react-popups'
import { useNavigate } from 'react-router-dom'
import AddPaymentModal from '../Modals/AddPaymentModal/AddPaymentModal.component'
import ConfirmDeleteModal from '../Modals/ConfirmDeleteModal/ConfirmDeleteModal.component'
import { Patient } from '@dataTypes/PatientInfo.types'
import {
  mockPaymentData,
  PaymentData,
  PaymentMonthData,
  PaymentStatus,
  MockSavedPaymentMethod,
} from '@assets/mockdata/PatientTray/mockPaymentData'
import { SavedCard } from '@dataTypes/payment.types'
import { shouldUsePatientTrayMockData } from '@utils/patientTrayMocks.util'
import {
  deletePaymentMethod,
  listPaymentMethods,
  mapPaymentMethodApiToDisplay,
  setDefaultPaymentMethod,
  type SavedCardDisplay,
} from '@services/api/billing/payment-method.api'
import { extractApiError } from '@utils/apiError.util'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { RootState } from '@store/index'
import { fetchSidePanelAnalytics } from '@store/actions/billing.action'
import { editDeleteDDComponent } from './History.component'
import { ReactComponent as AddPaymentMethodIcon } from '@assets/images/Sidepanel/add-payment-method.svg'
import { ReactComponent as PaymentCollectIcon } from '@assets/images/Sidepanel/payment-collect.svg'
import { ReactComponent as PaymentEmailIcon } from '@assets/images/Sidepanel/payment-email.svg'
import { ReactComponent as PaymentAddOutlineIcon } from '@assets/images/Sidepanel/payment-add-outline.svg'
import { ReactComponent as PaymentDeleteIcon } from '@assets/images/Sidepanel/payment-delete.svg'
import { ReactComponent as FamilyEditIcon } from '@assets/images/Sidepanel/family-edit.svg'
import { ReactComponent as ArrowRightMonthIcon } from '@assets/images/Sidepanel/arrow-right-month.svg'
import { ReactComponent as ArrowDownMonthIcon } from '@assets/images/Sidepanel/arrow-down-month.svg'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useTheme } from '@context/ThemeContext'
import { Badge, type BadgeColor, type BadgeMode } from '@enamelai/design-system'
import {
  SkeletonBar,
  SkeletonCard,
  SkeletonList,
  SkeletonRow,
} from './SidePanelSkeleton'

const PAYMENT_LOADING_CONTAINER = '.sp-payment'

type PaymentMethodsProps = {
  patient: Patient | null
}

type ExtendedSavedCard = SavedCardDisplay & Partial<MockSavedPaymentMethod>

const PaymentSectionHeader: React.FC<{
  title: string
  actions?: React.ReactNode
}> = ({ title, actions }) => (
  <div className="sp-payment-section-header">
    <span className="sp-payment-section-title">{title}</span>
    {actions && <div className="sp-payment-header-actions">{actions}</div>}
  </div>
)

const PaymentIconButton: React.FC<{
  content: string
  onClick?: () => void
  disabled?: boolean
  ariaLabel: string
  children: React.ReactNode
}> = ({ content, onClick, disabled, ariaLabel, children }) => (
  <TooltipComponent content={content} position="TopCenter">
    <button
      type="button"
      className="sp-payment-icon-btn"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
    >
      {children}
    </button>
  </TooltipComponent>
)

const formatOverviewAmount = (value: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)

const BalanceStatCard: React.FC<{
  label: string
  primary?: number
  secondary?: number
  showPlus?: boolean
  isLoading?: boolean
}> = ({ label, primary, secondary, showPlus, isLoading }) => (
  <div className="sp-payment-stat-card">
    <span className="sp-payment-stat-label">{label}</span>
    <div className="sp-payment-stat-value-row">
      {isLoading && <SkeletonBar width="70%" height={16} />}
      {!isLoading && showPlus && (
        <PaymentAddOutlineIcon className="sp-payment-wallet-plus" aria-hidden />
      )}
      {primary != null && secondary != null ? (
        <span
          className="sp-payment-stat-range"
          aria-label={`${formatOverviewAmount(primary)} of ${formatOverviewAmount(secondary)}`}
        >
          <span className="sp-payment-stat-primary">
            {formatOverviewAmount(primary)}
          </span>
          <span className="sp-payment-stat-divider" aria-hidden>
            /
          </span>
          <span className="sp-payment-stat-secondary">
            {formatOverviewAmount(secondary)}
          </span>
        </span>
      ) : primary != null ? (
        <span className="sp-payment-stat-single">
          {formatNumber(primary, true)}
        </span>
      ) : null}
    </div>
  </div>
)

const getTransactionStatusDisplay = (
  status: PaymentStatus,
): { label: string; color: BadgeColor } => {
  if (status === 'Complete') return { label: 'Paid', color: 'pale-green' }
  if (status === 'Pending' || status === 'Follow-up') {
    return { label: 'Pending', color: 'orange' }
  }
  return { label: status, color: 'red' }
}

const formatExpiry = (month: string, year: string): string => {
  const normalizedYear = year.length === 2 ? `20${year}` : year
  return `${month}/${normalizedYear}`
}

const getPaymentMethodLabel = (card: ExtendedSavedCard): string => {
  if (card.displayLabel) return card.displayLabel
  if (card.methodType === 'bank') return 'Digital bank/wallet'
  return 'Debit/Credit card'
}

const isBankMethod = (card: ExtendedSavedCard): boolean =>
  card.methodType === 'bank'

const getCardKey = (card: SavedCardDisplay): string =>
  card.payment_method_id ?? card.lastFour

const canDeleteCard = (
  card: SavedCardDisplay,
  useMockPaymentData: boolean,
): boolean => {
  if (useMockPaymentData) return true
  return !!card.payment_method_id
}

const PaymentMethods: React.FC<PaymentMethodsProps> = ({ patient }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { resolvedTheme } = useTheme()
  const badgeMode: BadgeMode = resolvedTheme === 'dark' ? 'solid' : 'tinted'
  const selectedClinic = useAppSelector(
    (state: RootState) => state.clinic.selectedClinic,
  )
  const sidePanelAnalytics = useAppSelector(
    (state: RootState) => state.billing.sidePanelAnalytics,
  )
  const loadingSidePanelAnalytics = useAppSelector(
    (state: RootState) => state.billing.loadingSidePanelAnalytics,
  )
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false)
  const [paymentData, setPaymentData] = useState<PaymentData | null>(null)
  const [savedCards, setSavedCards] = useState<SavedCardDisplay[]>([])
  const [activeAccordionKey, setActiveAccordionKey] = useState<string | null>(
    'December 2024',
  )
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add')
  const [cardToEdit, setCardToEdit] = useState<SavedCardDisplay | null>(null)
  const [cardToDelete, setCardToDelete] = useState<SavedCardDisplay | null>(
    null,
  )
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isSettingDefault, setIsSettingDefault] = useState(false)
  const [isLoadingCards, setIsLoadingCards] = useState(false)

  const useMockPaymentData = shouldUsePatientTrayMockData(patient?.patient_id)
  const useApiForCards = !!patient && !!selectedClinic && !useMockPaymentData
  const useApiForSidePanel =
    !!patient?.patient_id && !!selectedClinic?.clinic_id && !useMockPaymentData

  const fetchSavedCards = useCallback(async (): Promise<void> => {
    if (!patient?.patient_id || !selectedClinic?.clinic_id) return
    setIsLoadingCards(true)
    try {
      const res = await listPaymentMethods(
        patient.patient_id,
        selectedClinic.clinic_id,
      )
      const methods = res.data?.payment_methods ?? []
      setSavedCards(methods.map(mapPaymentMethodApiToDisplay))
    } catch {
      setSavedCards([])
      showToast(
        createFailedToast(
          'Could not load payment methods',
          'Please try again.',
        ),
      )
    } finally {
      setIsLoadingCards(false)
    }
  }, [patient?.patient_id, selectedClinic?.clinic_id, showToast])

  useEffect(() => {
    if (patient && useMockPaymentData) {
      const mockData = mockPaymentData[patient.patient_id] || null
      setPaymentData(mockData)
      setSavedCards((mockData?.savedCards ?? []) as SavedCardDisplay[])
    } else if (patient) {
      setPaymentData(null)
    } else {
      setPaymentData(null)
    }
  }, [patient, useMockPaymentData])

  useEffect(() => {
    if (!useApiForCards && !useMockPaymentData) {
      setSavedCards([])
    }
  }, [useApiForCards, useMockPaymentData])

  useEffect(() => {
    if (useApiForCards) {
      fetchSavedCards()
    }
  }, [useApiForCards, fetchSavedCards])

  useEffect(() => {
    if (useApiForSidePanel) {
      dispatch(fetchSidePanelAnalytics())
    }
  }, [dispatch, useApiForSidePanel])

  const isPaymentNetworkBusy =
    !useMockPaymentData && useApiForCards && (isDeleting || isSettingDefault)
  const showCardsSkeleton =
    useApiForCards && isLoadingCards && savedCards.length === 0

  useEffect(() => {
    if (isPaymentNetworkBusy) {
      showLoading({ container: PAYMENT_LOADING_CONTAINER })
    } else {
      hideLoading(PAYMENT_LOADING_CONTAINER)
    }
    return () => hideLoading(PAYMENT_LOADING_CONTAINER)
  }, [isPaymentNetworkBusy, showLoading, hideLoading])

  useEffect(() => {
    const history = paymentData?.paymentHistory ?? []
    const decemberMonth = history.find((m) => m.month === 'December 2024')
    setActiveAccordionKey(decemberMonth?.month ?? history[0]?.month ?? null)
  }, [patient?.patient_id, paymentData?.paymentHistory])

  const data: PaymentMonthData[] = paymentData?.paymentHistory || []
  const outstandingBalance = paymentData?.outstandingBalance
  const resolvedSidePanelAnalytics = useApiForSidePanel
    ? sidePanelAnalytics
    : null

  const shouldShowApiLoading = useApiForSidePanel && loadingSidePanelAnalytics
  const outstandingCurrent = shouldShowApiLoading
    ? undefined
    : useApiForSidePanel
      ? (resolvedSidePanelAnalytics?.insuranceBalance ?? 0) +
        (resolvedSidePanelAnalytics?.patientBalance ?? 0)
      : outstandingBalance?.current
  const outstandingTotal = shouldShowApiLoading
    ? undefined
    : useApiForSidePanel
      ? (resolvedSidePanelAnalytics?.outstandingTotal ?? 0)
      : outstandingBalance?.total
  const insuranceUsed = shouldShowApiLoading
    ? undefined
    : useApiForSidePanel
      ? (resolvedSidePanelAnalytics?.insuranceBalance ?? 0)
      : outstandingBalance?.insuranceCoverage?.used
  const insuranceTotal = shouldShowApiLoading
    ? undefined
    : useApiForSidePanel
      ? (resolvedSidePanelAnalytics?.insuranceTotal ?? 0)
      : outstandingBalance?.insuranceCoverage?.total
  const patientBalanceAmount = shouldShowApiLoading
    ? undefined
    : useApiForSidePanel
      ? (resolvedSidePanelAnalytics?.patientBalance ?? 0)
      : outstandingBalance?.patientBalance
  const familyBalanceAmount = shouldShowApiLoading
    ? undefined
    : useApiForSidePanel
      ? (resolvedSidePanelAnalytics?.familyBalance ?? 0)
      : outstandingBalance?.familyBalance
  const discountApplied = shouldShowApiLoading
    ? undefined
    : useApiForSidePanel
      ? (resolvedSidePanelAnalytics?.discountApplied ?? 0)
      : outstandingBalance?.discountApplied
  const walletBalance = shouldShowApiLoading
    ? undefined
    : useApiForSidePanel
      ? (resolvedSidePanelAnalytics?.walletBalance ?? 0)
      : outstandingBalance?.walletBalance

  const defaultCard = savedCards.find((card) => card.isDefault) || savedCards[0]

  const toggleAddPaymentClick = (): void => {
    setModalMode('add')
    setCardToEdit(null)
    setShowAddPaymentModal(!showAddPaymentModal)
  }

  const handleEditClick = (): void => {
    if (!defaultCard) return
    setModalMode('edit')
    setCardToEdit(defaultCard)
    setShowAddPaymentModal(true)
  }

  const handleRequestDeleteCard = (card: SavedCardDisplay): void => {
    setCardToDelete(card)
    setShowDeleteModal(true)
  }

  const handleCloseDeleteModal = (): void => {
    setShowDeleteModal(false)
    setCardToDelete(null)
  }

  const handleConfirmDeleteCard = async (): Promise<void> => {
    if (!cardToDelete || isDeleting) return

    const label = getPaymentMethodLabel(cardToDelete as ExtendedSavedCard)
    const cardKey = getCardKey(cardToDelete)

    setIsDeleting(true)
    try {
      if (useMockPaymentData) {
        setSavedCards((cards) =>
          cards.filter((card) => getCardKey(card) !== cardKey),
        )
        showToast(
          createSuccessToast(
            'Payment method removed',
            `"${label}" has been removed from saved payment methods.`,
          ),
        )
        handleCloseDeleteModal()
        return
      }

      if (!cardToDelete.payment_method_id) {
        showToast(
          createFailedToast(
            'Could not remove payment method',
            'This payment method cannot be removed.',
          ),
        )
        return
      }

      await deletePaymentMethod(cardToDelete.payment_method_id)
      await fetchSavedCards()
      showToast(
        createSuccessToast(
          'Payment method removed',
          `"${label}" has been removed from saved payment methods.`,
        ),
      )
      handleCloseDeleteModal()
    } catch (err: unknown) {
      showToast(
        createFailedToast(
          'Could not remove payment method',
          extractApiError(err, 'Please try again.'),
        ),
      )
    } finally {
      setIsDeleting(false)
    }
  }

  const handleSetDefaultCard = async (
    card: SavedCardDisplay,
  ): Promise<void> => {
    if (card.isDefault || isSettingDefault) return

    const label = getPaymentMethodLabel(card as ExtendedSavedCard)
    const cardKey = getCardKey(card)

    setIsSettingDefault(true)
    try {
      if (useMockPaymentData) {
        setSavedCards((cards) =>
          cards.map((item) => ({
            ...item,
            isDefault: getCardKey(item) === cardKey,
          })),
        )
        showToast(
          createSuccessToast(
            'Default payment method updated',
            `"${label}" is now your default payment method.`,
          ),
        )
        return
      }

      if (
        !card.payment_method_id ||
        !patient?.patient_id ||
        !selectedClinic?.clinic_id
      ) {
        showToast(
          createFailedToast(
            'Could not set default payment method',
            'This payment method cannot be set as default.',
          ),
        )
        return
      }

      await setDefaultPaymentMethod(
        card.payment_method_id,
        patient.patient_id,
        selectedClinic.clinic_id,
      )
      await fetchSavedCards()
      showToast(
        createSuccessToast(
          'Default payment method updated',
          `"${label}" is now your default payment method.`,
        ),
      )
    } catch (err: unknown) {
      showToast(
        createFailedToast(
          'Could not set default payment method',
          extractApiError(err, 'Please try again.'),
        ),
      )
    } finally {
      setIsSettingDefault(false)
    }
  }

  const handleSaveCard = (updatedCard: SavedCard): void => {
    if (modalMode === 'edit') {
      setSavedCards((cards) => {
        const nextCards = cards.map((card) =>
          card.lastFour === updatedCard.lastFour ? updatedCard : card,
        )
        if (updatedCard.isDefault) {
          return nextCards.map((card) => ({
            ...card,
            isDefault: card.lastFour === updatedCard.lastFour,
          }))
        }
        return nextCards
      })
      return
    }

    setSavedCards((cards) => {
      const nextCards = [...cards, updatedCard]
      if (updatedCard.isDefault) {
        return nextCards.map((card) => ({
          ...card,
          isDefault: card.lastFour === updatedCard.lastFour,
        }))
      }
      return nextCards
    })
  }

  const overviewActions = (
    <>
      <PaymentIconButton
        content="Collect"
        onClick={() => navigate('/billing/ledger')}
        ariaLabel="Collect payment"
      >
        <PaymentCollectIcon
          className="sp-payment-action-svg-icon"
          aria-hidden
        />
      </PaymentIconButton>
      <PaymentIconButton
        content="Email"
        disabled
        ariaLabel="Email payment request"
      >
        <PaymentEmailIcon className="sp-payment-action-svg-icon" aria-hidden />
      </PaymentIconButton>
    </>
  )

  const paymentMethodsActions = (
    <>
      <PaymentIconButton
        content="Add payment method"
        onClick={toggleAddPaymentClick}
        ariaLabel="Add payment method"
      >
        <AddPaymentMethodIcon
          className="sp-payment-action-svg-icon"
          aria-hidden
        />
      </PaymentIconButton>
      <PaymentIconButton
        content="Edit card is not available. To change card details, delete and add a new card."
        onClick={handleEditClick}
        disabled
        ariaLabel="Edit payment method"
      >
        <FamilyEditIcon className="sp-payment-action-svg-icon" aria-hidden />
      </PaymentIconButton>
    </>
  )

  return (
    <>
      <div className="sp-payment">
        <section className="sp-payment-section sp-payment-section--bordered">
          <PaymentSectionHeader
            title="Balance overview"
            actions={overviewActions}
          />
          <div className="sp-payment-stats-grid">
            <div className="sp-payment-stats-row">
              <BalanceStatCard
                isLoading={shouldShowApiLoading}
                label="Outstanding balance"
                primary={outstandingCurrent}
                secondary={outstandingTotal}
              />
              <BalanceStatCard
                isLoading={shouldShowApiLoading}
                label="Insurance coverage"
                primary={insuranceUsed}
                secondary={insuranceTotal}
              />
            </div>
            <div className="sp-payment-stats-row">
              <BalanceStatCard
                isLoading={shouldShowApiLoading}
                label="Patient balance"
                primary={patientBalanceAmount}
              />
              <BalanceStatCard
                isLoading={shouldShowApiLoading}
                label="Family balance"
                primary={familyBalanceAmount}
              />
            </div>
            <div className="sp-payment-stats-row">
              <BalanceStatCard
                isLoading={shouldShowApiLoading}
                label="Total disc. applied"
                primary={discountApplied}
              />
              <BalanceStatCard
                isLoading={shouldShowApiLoading}
                label="Wallet balance"
                primary={walletBalance}
                showPlus
              />
            </div>
          </div>
        </section>

        <section className="sp-payment-section sp-payment-section--bordered">
          <PaymentSectionHeader
            title="Payment Methods"
            actions={paymentMethodsActions}
          />
          {showCardsSkeleton ? (
            <SkeletonList>
              <SkeletonCard>
                <SkeletonRow>
                  <SkeletonBar width="45%" />
                  <SkeletonBar width={56} height={20} pill />
                </SkeletonRow>
                <SkeletonRow>
                  <SkeletonBar width="30%" />
                  <SkeletonBar width="25%" />
                </SkeletonRow>
              </SkeletonCard>
            </SkeletonList>
          ) : savedCards.length > 0 ? (
            <div className="sp-payment-methods-list">
              {savedCards.map((card) => {
                const extendedCard = card as ExtendedSavedCard
                const label = getPaymentMethodLabel(extendedCard)
                const isBank = isBankMethod(extendedCard)
                const showDelete = canDeleteCard(card, useMockPaymentData)

                return (
                  <div
                    key={card.payment_method_id ?? card.lastFour}
                    className="sp-payment-method-card"
                  >
                    <div className="sp-payment-method-card-top">
                      <span className="sp-payment-method-card-title">
                        {label}
                      </span>
                      {card.isDefault ? (
                        <Badge
                          label="Default"
                          color="pale-green"
                          mode={badgeMode}
                        />
                      ) : (
                        <button
                          type="button"
                          className="sp-payment-badge-btn"
                          onClick={() => void handleSetDefaultCard(card)}
                          disabled={isSettingDefault}
                          aria-label={`Set ${label} as default`}
                        >
                          <Badge
                            label="Set Default"
                            color="blue"
                            mode={badgeMode}
                          />
                        </button>
                      )}
                    </div>
                    {isBank ? (
                      <>
                        <div className="sp-payment-method-field-row">
                          <span className="sp-payment-method-field-label">
                            Bank Name:
                          </span>
                          <span className="sp-payment-method-field-value">
                            {extendedCard.bankName ?? '—'}
                          </span>
                        </div>
                        <div
                          className={`sp-payment-method-field-row${
                            showDelete
                              ? ' sp-payment-method-field-row--with-action'
                              : ''
                          }`}
                        >
                          <div className="sp-payment-method-field-main">
                            <span className="sp-payment-method-field-label">
                              Bank ID:
                            </span>
                            <span className="sp-payment-method-field-value">
                              *** *** {card.lastFour}
                            </span>
                          </div>
                          {showDelete ? (
                            <button
                              type="button"
                              className="sp-payment-method-delete-btn"
                              onClick={() => handleRequestDeleteCard(card)}
                              aria-label={`Remove ${label}`}
                            >
                              <PaymentDeleteIcon
                                className="sp-payment-method-delete-icon"
                                aria-hidden
                              />
                            </button>
                          ) : null}
                        </div>
                      </>
                    ) : (
                      <div
                        className={`sp-payment-method-field-row${
                          showDelete
                            ? ' sp-payment-method-field-row--with-action'
                            : ''
                        }`}
                      >
                        <div className="sp-payment-method-field-main sp-payment-method-field-main--expiry">
                          <span className="sp-payment-method-field-label">
                            Valid till
                          </span>
                          <span className="sp-payment-method-field-value">
                            {formatExpiry(card.expiryMonth, card.expiryYear)}
                          </span>
                        </div>
                        {showDelete ? (
                          <button
                            type="button"
                            className="sp-payment-method-delete-btn"
                            onClick={() => handleRequestDeleteCard(card)}
                            aria-label={`Remove ${label}`}
                          >
                            <PaymentDeleteIcon
                              className="sp-payment-method-delete-icon"
                              aria-hidden
                            />
                          </button>
                        ) : null}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          ) : (
            <p className="sp-payment-empty">No payment methods saved</p>
          )}
        </section>

        <section className="sp-payment-section">
          <PaymentSectionHeader title="Transactions" />
          <Accordion
            activeKey={activeAccordionKey ?? undefined}
            onSelect={(eventKey) => {
              if (typeof eventKey === 'string') {
                setActiveAccordionKey(eventKey)
              } else if (eventKey === null || eventKey === undefined) {
                setActiveAccordionKey(null)
              }
            }}
            className="sp-history-month-accordion"
          >
            {data.length > 0 ? (
              data.map((monthData) => (
                <Accordion.Item
                  eventKey={monthData.month}
                  key={monthData.month}
                >
                  <Accordion.Header>
                    <span className="sp-history-month-arrow" aria-hidden>
                      <ArrowRightMonthIcon className="sp-history-month-arrow-icon sp-history-month-arrow-icon--right" />
                      <ArrowDownMonthIcon className="sp-history-month-arrow-icon sp-history-month-arrow-icon--down" />
                    </span>
                    <span className="sp-history-month-label">
                      {monthData.month}
                    </span>
                  </Accordion.Header>
                  <Accordion.Body>
                    {monthData.items.length > 0 ? (
                      <div className="sp-payment-transaction-list">
                        {monthData.items.map((item, itemIdx) => {
                          const statusDisplay = getTransactionStatusDisplay(
                            item.status,
                          )
                          return (
                            <div
                              key={`${monthData.month}-${itemIdx}`}
                              className="sp-payment-transaction-card"
                            >
                              <div className="sp-payment-transaction-top">
                                <div className="sp-payment-transaction-title-row">
                                  <span className="sp-payment-transaction-code">
                                    {item.code}
                                  </span>
                                  <span className="sp-payment-transaction-doctor">
                                    {item.doctor}
                                  </span>
                                </div>
                                <span className="sp-payment-transaction-date">
                                  {item.date}
                                </span>
                              </div>
                              <div className="sp-payment-transaction-bottom">
                                <span className="sp-payment-transaction-amount">
                                  {formatNumber(item.amount, true)}
                                </span>
                                <div className="sp-payment-transaction-actions">
                                  <Badge
                                    label={statusDisplay.label}
                                    color={statusDisplay.color}
                                    mode={badgeMode}
                                  />
                                  {editDeleteDDComponent(
                                    `${monthData.month}-${itemIdx}`,
                                  )}
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    ) : (
                      <p className="sp-payment-empty sp-payment-empty--nested">
                        No transactions this month
                      </p>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              ))
            ) : (
              <p className="sp-payment-empty">No payment history available</p>
            )}
          </Accordion>
        </section>
      </div>
      <AddPaymentModal
        show={showAddPaymentModal}
        onHide={toggleAddPaymentClick}
        mode={modalMode}
        initialCard={cardToEdit}
        onSubmit={handleSaveCard}
        patientId={patient?.patient_id}
        clinicId={selectedClinic?.clinic_id}
        onCardAdded={useApiForCards ? fetchSavedCards : undefined}
      />
      <ConfirmDeleteModal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        onConfirm={() => void handleConfirmDeleteCard()}
        title="Remove payment method"
        bodyText={
          cardToDelete
            ? `Remove "${getPaymentMethodLabel(cardToDelete as ExtendedSavedCard)}" from saved payment methods? This cannot be undone.`
            : undefined
        }
        confirmButtonText={isDeleting ? 'Removing...' : 'Remove'}
        confirmDisabled={isDeleting}
      />
    </>
  )
}

export default PaymentMethods
